import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Printer, Download, Loader2, AlertCircle } from 'lucide-react';
import { ordenesEntregaService } from '../../../services/api';
import ordenEntregaTemplateHtml from './ordenEntregaTemplate.js';

const ESTADO_LABEL = {
  pendiente: 'Pendiente',
  en_despacho: 'En despacho',
  entregada_parcial: 'Entregada parcial',
  entregada: 'Entregada',
  anulada: 'Anulada'
};

// confirmacionId: si se pasa, el PDF muestra SOLO lo entregado en esa
// confirmacion puntual (recibo de una entrega parcial); sin el prop,
// muestra el acumulado de toda la orden (A6: "si es una entrega parcial,
// el PDF muestra lo entregado en esa entrega y lo que queda pendiente").
const OrdenEntregaPrintModal = ({ ordenId, confirmacionId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [htmlDoc, setHtmlDoc] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await ordenesEntregaService.getById(ordenId);
        if (isMounted) setHtmlDoc(response.data);
      } catch (err) {
        if (isMounted) setError(err.message || 'No se pudo cargar la orden de entrega');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchData();
    return () => { isMounted = false; };
  }, [ordenId]);

  const construirDatos = useCallback((oe) => {
    const confirmacion = confirmacionId
      ? (oe.confirmaciones || []).find(c => c.id === confirmacionId)
      : null;
    const indiceConfirmacion = confirmacion
      ? (oe.confirmaciones || []).findIndex(c => c.id === confirmacionId) + 1
      : null;

    const viaLabel = oe.via_entrega === 'nacional' ? 'Nacional'
      : oe.via_entrega === 'internacional' ? 'Internacional' : '';

    const items = (oe.items || []).map(item => {
      const cantidadMostrar = confirmacion
        ? (confirmacion.items || []).find(ci => ci.orden_entrega_item_id === item.id)?.cantidad || 0
        : item.cantidad_entregada;
      const pendiente = (parseFloat(item.cantidad_pedida) - parseFloat(item.cantidad_entregada)).toFixed(4).replace(/\.?0+$/, '') || '0';
      return {
        descripcion: item.descripcion,
        unidad: item.unidad,
        ubicacion: item.ubicacion_almacen,
        es_servicio: item.es_servicio,
        cantidad_pedida: item.cantidad_pedida,
        cantidad_mostrar: cantidadMostrar,
        pendiente
      };
    });

    return {
      numero: oe.numero,
      fecha: oe.fecha_emision ? new Date(oe.fecha_emision).toLocaleDateString('es-VE') : '',
      cotizacion_numero: oe.cotizacion_numero,
      factura_numero: oe.numero_factura,
      solicitud_folio: oe.solicitud_folio,
      cliente_nombre: oe.cliente_nombre || oe.empresa_nombre,
      cliente_rif: oe.cliente_rif || oe.cliente_cedula || oe.empresa_rif,
      cliente_direccion: oe.cliente_direccion,
      cliente_telefono: oe.cliente_telefono,
      via_entrega: viaLabel,
      fecha_estimada_entrega: oe.fecha_estimada_entrega ? new Date(oe.fecha_estimada_entrega).toLocaleDateString('es-VE') : '',
      es_parcial: !!confirmacion,
      numero_confirmacion: indiceConfirmacion,
      receptor_nombre: confirmacion?.receptor_nombre,
      receptor_cedula: confirmacion?.receptor_cedula,
      fecha_confirmacion: confirmacion ? new Date(confirmacion.fecha_real).toLocaleDateString('es-VE') : '',
      items,
      resumen: `Estado de la orden: ${ESTADO_LABEL[oe.estado] || oe.estado}`
    };
  }, [confirmacionId]);

  const handleIframeLoad = useCallback(() => {
    if (iframeRef.current && htmlDoc) {
      try {
        iframeRef.current.contentWindow.renderOrdenEntrega(construirDatos(htmlDoc));
      } catch (err) {
        console.error('Error al inyectar datos en el iframe:', err);
      }
    }
  }, [htmlDoc, construirDatos]);

  useEffect(() => { handleIframeLoad(); }, [handleIframeLoad]);

  const handlePrint = () => {
    if (iframeRef.current) iframeRef.current.contentWindow.print();
  };

  const handleDownload = () => {
    if (iframeRef.current && typeof iframeRef.current.contentWindow.descargarPDF === 'function') {
      iframeRef.current.contentWindow.descargarPDF();
    } else if (iframeRef.current) {
      iframeRef.current.contentWindow.print();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden">
        <div className="bg-slate-100 border-b p-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Printer className="text-slate-600" />
              Orden de Entrega {confirmacionId ? '— recibo de entrega parcial' : ''}
            </h2>
            <p className="text-sm text-slate-500">Orden ID: {ordenId}</p>
          </div>
          <div className="flex gap-2">
            {!loading && !error && (
              <>
                <button onClick={handleDownload} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium flex items-center gap-2">
                  <Download size={18} /> Descargar
                </button>
                <button onClick={handlePrint} className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 font-medium flex items-center gap-2">
                  <Printer size={18} /> Imprimir
                </button>
              </>
            )}
            <button onClick={onClose} className="px-4 py-2 bg-slate-200 text-slate-800 rounded-lg hover:bg-slate-300 font-medium flex items-center gap-2">
              <X size={18} /> Cerrar
            </button>
          </div>
        </div>

        <div className="flex-1 bg-slate-200 relative overflow-hidden flex justify-center items-center">
          {loading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
              <Loader2 size={48} className="animate-spin text-brand-600 mb-4" />
              <p className="text-lg font-medium text-slate-700">Generando documento...</p>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white">
              <AlertCircle size={64} className="text-red-500 mb-4" />
              <p className="text-xl font-bold text-slate-800 mb-2">Error al cargar datos</p>
              <p className="text-slate-600 max-w-md text-center">{error}</p>
            </div>
          )}
          <iframe
            ref={iframeRef}
            srcDoc={ordenEntregaTemplateHtml}
            onLoad={handleIframeLoad}
            className={`w-full h-full bg-white transition-opacity duration-300 ${loading || error ? 'opacity-0' : 'opacity-100'}`}
            title="Vista de impresión de la Orden de Entrega"
          />
        </div>
      </div>
    </div>
  );
};

export default OrdenEntregaPrintModal;
